import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbLd } from "@/lib/seo";
import { EDITION } from "@/lib/edition";
import WhatsAppGroupBox from "@/components/WhatsAppGroupBox";
import ShareButton from "@/components/ShareButton";
import InstitutionPeople from "@/components/InstitutionPeople";
import PhotoArchive from "@/components/PhotoArchive";
import SaveInstitutionButton from "@/components/SaveInstitutionButton";
import AdmissionAlerts from "@/components/AdmissionAlerts";
import MemoryLanePromo from "@/components/MemoryLanePromo";import InstitutionTeam from "@/components/InstitutionTeam";
import InstitutionMemoryWall from "@/components/InstitutionMemoryWall";
import InstitutionMemorials from "@/components/InstitutionMemorials";
import InviteDialog from "@/components/InviteDialog";
import { toast } from "sonner";
import {
  Buildings, MapPin, CalendarBlank, UsersThree, ArrowLeft, Globe, SealCheck, UsersFour,
  BookOpen, Plus, PencilSimple, ShieldCheck, ArrowSquareOut, MagnifyingGlass, Sparkle, Handshake,
  Images, IdentificationCard, X, ArrowRight, Flag, CalendarPlus, ArrowsMerge,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import InstitutionImpact from "@/components/InstitutionImpact";
import InstitutionLocalization from "@/components/InstitutionLocalization";
import TalkVibes from "@/components/TalkVibes";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_META = {
  officially_managed: { label: "Officially managed", cls: "bg-secondary text-secondary-foreground", icon: ShieldCheck },
  verified: { label: "Verified", cls: "bg-primary/10 text-primary", icon: SealCheck },
  auto_discovered: { label: "Auto-discovered", cls: "bg-muted text-muted-foreground", icon: Sparkle },
  community: { label: "Community sourced", cls: "bg-muted text-muted-foreground", icon: UsersThree },
};

// Shown once, right after a visitor returns here from signing up (flag set in Register.js).
function JustJoinedBanner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    try {
      if (sessionStorage.getItem("ia_just_joined") === "1") {
        sessionStorage.removeItem("ia_just_joined");
        setShow(true);
      }
    } catch { /* ignore */ }
  }, []);
  if (!show) return null;
  return (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-2xl border border-secondary/40 bg-secondary/10 px-4 py-3" data-testid="just-joined-banner">
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-secondary text-secondary-foreground"><Sparkle size={16} weight="fill" /></span>
        <p className="text-sm font-medium text-foreground">You're in! Scroll down to share a memory or find a classmate.</p>
      </div>
      <button onClick={() => setShow(false)} className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-secondary/20" data-testid="just-joined-dismiss" aria-label="Dismiss"><X size={16} /></button>
    </div>
  );
}

export default function InstitutionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [inst, setInst] = useState(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claim, setClaim] = useState({ role_title: "", work_email: "", evidence: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState({});
  const [courseOpen, setCourseOpen] = useState(false);
  const [course, setCourse] = useState({ name: "" });
  const [discovering, setDiscovering] = useState(false);
  const [saved, setSaved] = useState(false);
  const photosRef = useRef();

  const load = () => api.get(`/institutions/${id}`).then((r) => setInst(r.data)).catch(() => setInst(false));
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!user) { setSaved(false); return; }
    api.get("/profiles/me").then((r) => setSaved((r.data.saved_institutions || []).some((s) => s.institution_id === id))).catch(() => {});
  }, [id, user]);
  const _ok = inst && inst !== false;
  const _canonPath = `/institutions/${id}`;
  useSeo(
    _ok ? inst.name : "Institution",
    _ok ? (inst.description || `${inst.name} — alumni community, batches, teachers, memories and reunions on ${EDITION.brand}.`) : null,
    _ok ? inst.logo_url : null,
    _ok ? {
      canonicalPath: _canonPath,
      type: "profile",
      jsonLd: [{
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        name: inst.name,
        description: inst.description || undefined,
        url: `${EDITION.url}${_canonPath}`,
        ...(inst.logo_url ? { logo: inst.logo_url } : {}),
        ...(inst.website ? { sameAs: [inst.website] } : {}),
        ...((inst.city || inst.state || inst.country) ? {
          address: {
            "@type": "PostalAddress",
            ...(inst.city ? { addressLocality: inst.city } : {}),
            ...(inst.state ? { addressRegion: inst.state } : {}),
            ...(inst.country ? { addressCountry: inst.country } : {}),
          },
        } : {}),
      },
      breadcrumbLd([
        ["Home", "/"],
        ["Institutions", "/explore/institutions"],
        [inst.name, _canonPath],
      ])],
    } : undefined,
  );
  if (inst === null) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  if (inst === false) return <div className="p-12 text-center text-muted-foreground">Institution not found.</div>;

  const sm = STATUS_META[inst.claim_status] || STATUS_META.community;
  const StatusIcon = sm.icon;
  const canManage = ["owner", "manager", "admin"].includes(inst.my_role);

  const submitClaim = async () => {
    if (!claim.role_title || !claim.work_email) return toast.error("Role and work email are required");
    try {
      await api.post(`/institutions/${id}/claim`, claim);
      toast.success("Claim submitted — an admin will review it");
      setClaimOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = () => { setEdit({ name: inst.name, type: inst.type, description: inst.description, location: inst.location, country: inst.country, state: inst.state, district: inst.district, website: inst.website, founded: inst.founded, logo_url: inst.logo_url, departments: inst.departments || [], faculty: inst.faculty || [], photos: inst.photos || [] }); setEditOpen(true); };
  const submitEdit = async () => {
    try { await api.put(`/institutions/${id}`, edit); toast.success("Institution updated"); setEditOpen(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const resetSite = async () => {
    if (!window.confirm("Remove the current website from this institution? This clears wrongly-attached site content. You can build a fresh one afterwards.")) return;
    try { await api.post(`/institutions/${id}/site/detach`); toast.success("Website reset — you can build a fresh one now"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const uploadPhotos = async (files) => {
    const ids = [];
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); ids.push(data.id); } catch {}
    }
    setEdit((e) => ({ ...e, photos: [...(e.photos || []), ...ids] }));
    toast.success(`${ids.length} photo(s) added`);
  };
  const convertGroup = async (gid) => {
    try { const { data } = await api.post(`/discovered-groups/${gid}/convert`); toast.success("Community created!"); nav(`/groups/${data.group_id}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const submitCourse = async () => {
    if (!course.name) return toast.error("Course name is required");
    try { await api.post("/courses", { ...course, institution_id: id }); toast.success("Course added"); setCourseOpen(false); setCourse({ name: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runDiscovery = async () => {
    setDiscovering(true);
    try { const { data } = await api.post(`/crawler/institutions/${id}/discover-groups`); toast.success(`Found ${data.found} links · ${data.added} new alumni groups`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDiscovering(false); }
  };

  return (
    <main className="mx-auto max-w-5xl px-5 py-8">
      <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={16} /> Institutions</button>

      <JustJoinedBanner />

      {inst.groups?.length > 0 && <JoinAlumniBanner inst={inst} group={inst.groups[0]} />}

      <Card className="overflow-hidden border-slate-100 shadow-sm">
        <div className="relative h-52 bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500">
          {inst.logo_url && <img src={inst.logo_url} alt={inst.name} className="h-full w-full object-cover opacity-45" />}
          <div className="absolute inset-0 bg-gradient-to-t from-green-900/50 to-transparent" />
          <div className="absolute bottom-5 left-6 right-6 flex flex-wrap items-end justify-between gap-3 text-white">
            <div>
              <h1 className="font-heading text-3xl font-medium">{inst.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Badge className="rounded-full bg-white/20 font-normal backdrop-blur">{inst.type}</Badge>
                <Badge className={`gap-1 rounded-full font-normal ${sm.cls}`} data-testid="inst-status"><StatusIcon size={12} weight="fill" /> {sm.label}</Badge>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {inst.is_manager && <Button variant="secondary" size="sm" className="rounded-full gap-1.5" onClick={() => nav(`/institutions/${id}/build`)} data-testid="build-website-btn"><Globe size={15} /> {inst.site?.slug ? "Edit website" : "Build website"}</Button>}
              {inst.site?.published && inst.site?.slug && <Link to={`/site/${inst.site.slug}`} target="_blank"><Button variant="secondary" size="sm" className="rounded-full gap-1.5" data-testid="view-website-btn"><ArrowSquareOut size={15} /> View site</Button></Link>}
              {inst.is_manager && inst.site?.slug && (
                <Button variant="secondary" size="sm" className="rounded-full gap-1.5" onClick={resetSite} data-testid="reset-website-btn"><Flag size={15} /> Reset website</Button>
              )}
              {user?.role === "admin" && <MergeDialog keepInst={inst} onMerged={load} />}
              {inst.is_manager && <Button variant="secondary" size="sm" className="rounded-full gap-1.5" onClick={openEdit} data-testid="manage-institution-btn"><PencilSimple size={15} /> Manage</Button>}
              <ShareButton targetPath={`/institutions/${inst.id}`} entityType="institution" entityId={inst.id} title={inst.name} variant="secondary" />
              <AdmissionAlerts instId={inst.id} instName={inst.name} canManage={canManage} />
              {inst.local_lang && (
                <Button variant="secondary" size="sm" className="rounded-full gap-1.5" data-testid="localized-jump-btn"
                  onClick={() => document.getElementById("localized-section")?.scrollIntoView({ behavior: "smooth" })}>
                  <Globe size={15} /> {inst.local_lang_label}
                </Button>
              )}
              <SaveInstitutionButton instId={inst.id} saved={saved} onToggle={(_, s) => setSaved(s)} variant="secondary" stopPropagation={false} />
              <InviteDialog type="institution" id={inst.id} label="Invite card" variant="secondary" size="sm" />
              <CorrectionDialog instId={inst.id} inst={inst} />
            </div>
          </div>
        </div>

        <div className="p-6 sm:p-8">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {[
              { icon: MapPin, label: "Location", value: inst.location || "—" },
              { icon: Globe, label: "Country", value: inst.country || "—" },
              { icon: CalendarBlank, label: "Founded", value: inst.founded || "—" },
              { icon: UsersThree, label: "Alumni", value: inst.alumni_count ?? 0 },
            ].map((s) => (
              <div key={s.label} className="rounded-xl border border-border/60 p-4">
                <s.icon className="text-secondary" size={20} weight="duotone" />
                <div className="mt-2 font-heading text-lg font-medium">{s.value}</div>
                <div className="text-xs text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
          {inst.description && (!inst.enriched_article || inst.enriched_article.length <= (inst.description || "").length + 20) && <p className="mt-6 max-w-2xl leading-relaxed text-foreground/90">{inst.description}</p>}

          {(inst.board || inst.management || inst.educational_district || inst.district) && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="school-facts">
              {inst.board && <Badge variant="outline" className="rounded-full border-secondary/40 text-secondary">Board: {inst.board}</Badge>}
              {inst.management && inst.management !== "—" && <Badge variant="outline" className="rounded-full">{inst.management}</Badge>}
              {inst.educational_district && <Badge variant="outline" className="rounded-full">Edu. district: {inst.educational_district}</Badge>}
              {inst.district && <Badge variant="outline" className="rounded-full">{inst.district} district</Badge>}
              {inst.verification_status === "community_sourced" && <Badge variant="outline" className="rounded-full border-amber-400/50 text-amber-700">Community-sourced</Badge>}
            </div>
          )}

          {inst.enriched_article && inst.enriched_article.length > (inst.description || "").length + 20 && (
            <div className="mt-5 max-w-2xl space-y-3 rounded-xl border border-border/60 bg-muted/30 p-4" data-testid="enriched-article">
              {inst.enriched_article.split("\n").filter(Boolean).map((para, i) => (
                <p key={i} className="text-sm leading-relaxed text-foreground/90">{para}</p>
              ))}
            </div>
          )}

          {Array.isArray(inst.sources) && inst.sources.length > 0 && (
            <div className="mt-4 max-w-2xl" data-testid="inst-sources">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sources</div>
              <ul className="mt-1.5 space-y-1">
                {inst.sources.map((src, i) => {
                  const isObj = src && typeof src === "object";
                  const label = isObj ? (src.name === "Wikipedia" && src.title ? `Wikipedia — ${src.title}` : (src.name || src.title || "Source")) : String(src);
                  const url = isObj ? src.url : null;
                  return (
                    <li key={i}>
                      {url ? (
                        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-secondary hover:underline">
                          <ArrowSquareOut size={12} /> {label}
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">{label}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {inst.enrichment_status === "ai" && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs text-amber-700" data-testid="ai-assisted-note">
              <Sparkle size={12} weight="fill" /> AI-assisted summary — spotted something off? Use “Suggest a correction” above.
            </div>
          )}
          {inst.website && <a href={inst.website.startsWith("http") ? inst.website : `https://${inst.website}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-sm text-secondary hover:underline"><ArrowSquareOut size={14} /> Official website</a>}

          {inst.suggested_by && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-secondary/10 px-3 py-1.5 text-xs text-secondary" data-testid="added-with-help">
              <Handshake size={14} weight="duotone" /> Added with help from <span className="font-medium">{inst.suggested_by}</span>
            </div>
          )}

          {/* Claim nudge — crowdsource accuracy on auto-drafted pages */}
          {!inst.is_manager && inst.claim_status !== "officially_managed" && ["ai", "wikipedia"].includes(inst.enrichment_status) && !inst.claim_pending && (
            <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300/60 bg-amber-50 p-4" data-testid="claim-nudge-banner">
              <Handshake className="shrink-0 text-amber-600" size={22} weight="duotone" />
              <p className="flex-1 text-sm text-amber-900">Are you from <span className="font-medium">{inst.name}</span>? This page was drafted automatically — claim it to correct the details, add photos and list courses.</p>
              <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700" onClick={() => (user ? setClaimOpen(true) : nav("/login"))} data-testid="claim-nudge-btn">Claim &amp; improve this page</Button>
            </div>
          )}

          {/* Claim CTA */}
          {inst.can_claim && !["ai", "wikipedia"].includes(inst.enrichment_status) && (
            <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-secondary/30 bg-secondary/5 p-4">
              <Handshake className="text-secondary" size={22} weight="duotone" />
              <p className="flex-1 text-sm text-muted-foreground">Represent this institution? Claim it to verify details, add courses and manage its page.</p>
              <Button size="sm" className="rounded-full" onClick={() => setClaimOpen(true)} data-testid="claim-institution-btn">Claim this institution</Button>
            </div>
          )}
          {inst.claim_pending && <p className="mt-4 text-sm italic text-muted-foreground">Your claim is under review.</p>}
          {!user && <p className="mt-4 text-sm text-muted-foreground"><Link to="/login" className="text-secondary hover:underline">Sign in</Link> to connect with alumni, add courses or claim this institution.</p>}
        </div>
      </Card>

      <InstitutionImpact inst={inst} />

      <InstitutionLocalization inst={inst} canManage={canManage} />

      <div className="mt-6" data-testid="institution-talk">
        <TalkVibes entityType="institution" entityId={inst.id} title={inst.name}
          subtitle="Campus & alumni talk — memories, questions, reunions, photos & news."
          accent="institution"
          memberHint={(() => { const ag = (inst.groups || []).find((g) => g.type === "association") || (inst.groups || [])[0]; return ag ? `${ag.member_count || 0} alumni` : null; })()} />
      </div>

      <BatchBoards inst={inst} />

      {/* People & Legacy — teachers, faculty & staff */}
      <Card className="mt-6 border-border/60 p-6 shadow-sm" data-testid="people-section">
        <InstitutionPeople institutionId={inst.id} institutionName={inst.name} canManage={canManage} />
      </Card>

      <Card className="mt-6 border-border/60 p-6 shadow-sm" data-testid="photo-archive-section">
        <PhotoArchive scopeType="institution" scopeId={inst.id} scopeName={inst.name} />
      </Card>

      {/* Alumni Community — prominent, moved up to encourage joining & inviting */}
      <Card className="mt-6 border-green-200 bg-gradient-to-br from-green-50 to-white p-6 shadow-sm" data-testid="alumni-groups-section">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-heading text-xl font-medium"><UsersFour className="text-green-600" size={22} weight="duotone" /> Alumni Groups</h2>
          {canManage && (
            <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={runDiscovery} disabled={discovering} data-testid="discover-groups-btn">
              <MagnifyingGlass size={15} /> {discovering ? "Searching…" : "Find alumni groups"}
            </Button>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {(inst.groups || []).map((g) => (
            <div key={g.id} className="rounded-2xl border border-green-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md" data-testid={`inst-group-${g.id}`}>
              <div className="flex items-start gap-2">
                <UsersFour size={20} weight="duotone" className="mt-0.5 shrink-0 text-green-600" />
                <div className="min-w-0">
                  <div className="font-heading text-lg font-medium leading-tight">{g.name}</div>
                  <div className="text-xs capitalize text-green-800/70">{g.type} · Official community on AlumniPulse</div>
                </div>
              </div>
              <p className="mt-2 text-sm text-muted-foreground">Reconnect with fellow alumni, share memories and start discussions.</p>
              <GroupSocialStat groupId={g.id} />
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to={`/groups/${g.id}`}><Button size="sm" className="rounded-full gap-1.5 bg-green-600 text-white hover:bg-green-700" data-testid={`join-group-${g.id}`}>Join community <ArrowRight size={14} /></Button></Link>
                <PlanReunionButton groupId={g.id} />
                <InviteDialog type="group" id={g.id} label="Invite friends" variant="outline" size="sm" />
              </div>
              <WhatsAppGroupBox groupId={g.id} groupName={g.name} />
            </div>
          ))}
        </div>

        {inst.discovered_groups?.length > 0 && (
          <div className="mt-4">
            <p className="overline text-muted-foreground mb-2">Discovered around the web</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {inst.discovered_groups.map((g) => (
                <Card key={g.id} className="flex items-center justify-between gap-3 border-border/60 p-4" data-testid="discovered-group">
                  <a href={g.url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1">
                    <div className="truncate font-medium hover:underline">{g.name}</div>
                    <div className="text-xs text-muted-foreground">{g.official ? "Official Alumni Association" : "Independent / community group"} · unverified</div>
                  </a>
                  {g.converted && g.group_id ? (
                    <Link to={`/groups/${g.group_id}`}><Button size="sm" variant="outline" className="rounded-full gap-1.5">Open <ArrowRight size={14} /></Button></Link>
                  ) : canManage ? (
                    <Button size="sm" className="shrink-0 rounded-full gap-1.5" onClick={() => convertGroup(g.id)} data-testid="convert-group-btn"><Plus size={14} /> Create community</Button>
                  ) : (
                    <ArrowSquareOut className="shrink-0 text-secondary" size={16} />
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
        {(!inst.groups?.length && !inst.discovered_groups?.length) && <p className="text-sm text-muted-foreground">No communities yet for this institution.</p>}
      </Card>

      {/* Departments */}
      {inst.departments?.length > 0 && (
        <Card className="mt-6 border-border/60 p-6 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-medium"><Buildings className="text-secondary" size={22} weight="duotone" /> Departments</h2>
          <div className="flex flex-wrap gap-2">
            {inst.departments.map((d, i) => <Badge key={i} variant="secondary" className="rounded-full font-normal">{d}</Badge>)}
          </div>
        </Card>
      )}

      {/* Faculty */}
      {inst.faculty?.length > 0 && (
        <Card className="mt-6 border-border/60 p-6 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-medium"><IdentificationCard className="text-secondary" size={22} weight="duotone" /> Faculty</h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {inst.faculty.map((f, i) => (
              <div key={i} className="rounded-xl border border-border/60 p-4">
                <div className="font-medium">{f.name}</div>
                {f.title && <div className="text-sm text-muted-foreground">{f.title}</div>}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Campus photos */}
      {inst.photos?.length > 0 && (
        <Card className="mt-6 border-border/60 p-6 shadow-sm">
          <h2 className="mb-4 flex items-center gap-2 font-heading text-xl font-medium"><Images className="text-secondary" size={22} weight="duotone" /> Campus</h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {inst.photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="Campus" className="aspect-square w-full rounded-xl object-cover" />)}
          </div>
        </Card>
      )}

      {/* Courses */}
      <Card className="mt-6 border-border/60 p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading text-xl font-medium"><BookOpen className="text-secondary" size={22} weight="duotone" /> Courses & Programs</h2>
          {user && <Button size="sm" variant="outline" className="rounded-full gap-1.5" onClick={() => setCourseOpen(true)} data-testid="add-course-btn"><Plus size={15} /> Add course</Button>}
        </div>
        {(!inst.courses || inst.courses.length === 0) && <p className="text-sm text-muted-foreground">No courses listed yet. Add the first one.</p>}
        <div className="grid gap-3 sm:grid-cols-2">
          {(inst.courses || []).map((c) => (
            <Link key={c.id} to={`/courses/${c.public_slug || c.id}`} data-testid="course-card">
              <Card className="border-border/60 p-4 transition-colors hover:border-secondary/40">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{c.name}</div>
                  {c.verified && <SealCheck className="text-secondary" size={16} weight="fill" />}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
                  {c.degree_type && <span>{c.degree_type}</span>}{c.department && <span>· {c.department}</span>}{c.duration && <span>· {c.duration}</span>}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </Card>

      {/* Claim dialog */}
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Claim {inst.name}</DialogTitle>
            <DialogDescription>Tell us your role. An admin will review before granting management access.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Your role / title</Label><Input value={claim.role_title} onChange={(e) => setClaim({ ...claim, role_title: e.target.value })} placeholder="Alumni Relations Officer" data-testid="claim-role" /></div>
            <div className="space-y-1.5"><Label>Work email</Label><Input type="email" value={claim.work_email} onChange={(e) => setClaim({ ...claim, work_email: e.target.value })} placeholder="you@institution.edu" data-testid="claim-email" /></div>
            <div className="space-y-1.5"><Label>Evidence (optional)</Label><Textarea rows={2} value={claim.evidence} onChange={(e) => setClaim({ ...claim, evidence: e.target.value })} placeholder="Link to your staff page, LinkedIn, etc." /></div>
          </div>
          <DialogFooter><Button onClick={submitClaim} className="rounded-full" data-testid="claim-submit">Submit claim</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage/edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Manage institution</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Name</Label><Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-inst-name" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} data-testid="edit-inst-description" /></div>
            <div className="space-y-1.5"><Label>Location (display address)</Label><Input value={edit.location || ""} onChange={(e) => setEdit({ ...edit, location: e.target.value })} data-testid="edit-inst-location" /></div>
            <div className="space-y-1.5"><Label>Country</Label><Input value={edit.country || ""} onChange={(e) => setEdit({ ...edit, country: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>State / region</Label><Input value={edit.state || ""} onChange={(e) => setEdit({ ...edit, state: e.target.value })} placeholder="e.g. Kerala" data-testid="edit-inst-state" /></div>
            <div className="space-y-1.5"><Label>District</Label><Input value={edit.district || ""} onChange={(e) => setEdit({ ...edit, district: e.target.value })} placeholder="e.g. Kottayam" data-testid="edit-inst-district" /></div>
            <div className="space-y-1.5"><Label>Founded</Label><Input value={edit.founded || ""} onChange={(e) => setEdit({ ...edit, founded: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Website</Label><Input value={edit.website || ""} onChange={(e) => setEdit({ ...edit, website: e.target.value })} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Logo URL</Label><Input value={edit.logo_url || ""} onChange={(e) => setEdit({ ...edit, logo_url: e.target.value })} /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Departments (comma separated)</Label>
              <Input value={(edit.departments || []).join(", ")} onChange={(e) => setEdit({ ...edit, departments: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Computer Science, Mechanical, Physics" data-testid="edit-departments" /></div>
            <div className="space-y-2 sm:col-span-2">
              <div className="flex items-center justify-between"><Label>Faculty</Label>
                <button type="button" className="text-xs text-secondary hover:underline" onClick={() => setEdit({ ...edit, faculty: [...(edit.faculty || []), { name: "", title: "" }] })} data-testid="add-faculty">+ Add faculty</button></div>
              {(edit.faculty || []).map((f, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Name" value={f.name} onChange={(e) => { const fac = [...edit.faculty]; fac[i] = { ...fac[i], name: e.target.value }; setEdit({ ...edit, faculty: fac }); }} data-testid="faculty-name" />
                  <Input placeholder="Title" value={f.title} onChange={(e) => { const fac = [...edit.faculty]; fac[i] = { ...fac[i], title: e.target.value }; setEdit({ ...edit, faculty: fac }); }} />
                  <button type="button" onClick={() => setEdit({ ...edit, faculty: edit.faculty.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive"><X size={16} /></button>
                </div>
              ))}
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Campus photos</Label>
              <div className="flex flex-wrap gap-2">
                {(edit.photos || []).map((p, i) => (
                  <div key={i} className="relative">
                    <img src={fileUrl(p)} alt="campus" className="h-16 w-16 rounded-lg object-cover" />
                    <button type="button" onClick={() => setEdit({ ...edit, photos: edit.photos.filter((_, j) => j !== i) })} className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-destructive text-destructive-foreground"><X size={11} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => photosRef.current.click()} className="grid h-16 w-16 place-items-center rounded-lg border border-dashed border-border text-muted-foreground hover:bg-accent" data-testid="add-photos-btn"><Images size={20} /></button>
                <input ref={photosRef} type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files.length && uploadPhotos(Array.from(e.target.files))} data-testid="photos-input" />
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={submitEdit} className="rounded-full" data-testid="edit-inst-submit">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add course dialog */}
      <Dialog open={courseOpen} onOpenChange={setCourseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Add a course / program</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Course name *</Label><Input value={course.name} onChange={(e) => setCourse({ ...course, name: e.target.value })} placeholder="B.Tech Computer Science" data-testid="course-name" /></div>
            <div className="space-y-1.5"><Label>Degree type</Label><Input value={course.degree_type || ""} onChange={(e) => setCourse({ ...course, degree_type: e.target.value })} placeholder="Degree / Diploma / Certificate" /></div>
            <div className="space-y-1.5"><Label>Department</Label><Input value={course.department || ""} onChange={(e) => setCourse({ ...course, department: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Duration</Label><Input value={course.duration || ""} onChange={(e) => setCourse({ ...course, duration: e.target.value })} placeholder="4 years" /></div>
            <div className="space-y-1.5"><Label>Official course URL</Label><Input value={course.official_url || ""} onChange={(e) => setCourse({ ...course, official_url: e.target.value })} data-testid="course-official-url" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Textarea rows={2} value={course.description || ""} onChange={(e) => setCourse({ ...course, description: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submitCourse} className="rounded-full" data-testid="course-submit">Add course</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {inst.is_manager && <InstitutionTeam instId={inst.id} />}
      <InstitutionMemoryWall instName={inst.name} />
      <InstitutionMemorials instName={inst.name} />

      <MemoryLanePromo label={inst.name} className="mt-12" />
    </main>
  );
}


function JoinAlumniBanner({ inst, group }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/public/groups/${group.id}/stats`).then((r) => setS(r.data)).catch(() => {}); }, [group.id]);
  return (
    <div className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-gradient-to-r from-green-600 to-emerald-500 p-5 text-white shadow-md" data-testid="join-alumni-banner">
      <div className="flex items-center gap-3">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/20"><UsersFour size={26} weight="fill" /></span>
        <div>
          <div className="font-heading text-lg font-medium leading-tight">Join your {inst.name} alumni community</div>
          <p className="mt-0.5 text-sm text-white/90">
            {s ? (
              <>
                <span className="font-semibold">{s.member_count}</span> member{s.member_count === 1 ? "" : "s"}
                {s.recently_joined > 0 && <> · <span className="font-semibold">{s.recently_joined}</span> joined recently</>}
                {s.member_count === 0 && " · be the first to join and invite classmates"}
              </>
            ) : "Reconnect with classmates, share memories and start discussions."}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Link to={`/groups/${group.id}`}><Button size="sm" className="rounded-full gap-1.5 bg-white text-green-700 hover:bg-green-50" data-testid="banner-join-btn">Join now <ArrowRight size={14} /></Button></Link>
        <InviteDialog type="group" id={group.id} label="Invite classmates" variant="secondary" size="sm" />
      </div>
    </div>
  );
}

function PlanReunionButton({ groupId }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", venue: "", registration_enabled: true });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openDialog = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.info("Sign in to plan a reunion"); nav("/login"); return; }
    setOpen(true);
  };
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, date: form.date ? new Date(form.date).toISOString() : "" };
      const { data } = await api.post(`/groups/${groupId}/plan-reunion`, payload);
      toast.success("Reunion created! Continue planning in the group discussion.");
      nav(`/groups/${data.group_id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not create reunion"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" className="rounded-full gap-1.5 border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100" onClick={openDialog} data-testid={`plan-reunion-${groupId}`}>
        <CalendarPlus size={15} weight="fill" /> Plan a reunion
      </Button>
      <DialogContent className="sm:max-w-md" data-testid="reunion-dialog">
        <DialogHeader>
          <DialogTitle>Plan a reunion</DialogTitle>
          <DialogDescription>Set the details now, or leave them blank and finalise together in the group.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input className="mt-1" value={form.title} onChange={set("title")} placeholder="e.g. Class of 2005 Reunion" data-testid="reunion-title" /></div>
          <div><Label>Date &amp; time</Label><Input type="datetime-local" className="mt-1" value={form.date} onChange={set("date")} data-testid="reunion-date" /></div>
          <div><Label>Venue</Label><Input className="mt-1" value={form.venue} onChange={set("venue")} placeholder="Campus, hall, city or online link" data-testid="reunion-venue" /></div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={form.registration_enabled} onChange={(e) => setForm((f) => ({ ...f, registration_enabled: e.target.checked }))} className="h-4 w-4 accent-green-600" data-testid="reunion-rsvp" />
            Enable RSVP so members can confirm attendance
          </label>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="rounded-full gap-1.5 bg-green-600 text-white hover:bg-green-700" data-testid="reunion-submit"><CalendarPlus size={15} /> {busy ? "Creating…" : "Create reunion"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupSocialStat({ groupId }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/public/groups/${groupId}/stats`).then((r) => setS(r.data)).catch(() => {}); }, [groupId]);
  if (!s) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs" data-testid={`group-stats-${groupId}`}>
      <span className="inline-flex items-center gap-1 font-medium text-green-700"><UsersFour size={13} weight="fill" /> {s.member_count} member{s.member_count === 1 ? "" : "s"}</span>
      {s.recently_joined > 0
        ? <span className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2 py-0.5 font-medium text-orange-700">🔥 {s.recently_joined} joined recently</span>
        : s.member_count === 0
          ? <span className="text-muted-foreground">Be the first to join</span>
          : null}
    </div>
  );
}

const CORRECTION_FIELDS = [
  { value: "name", label: "Wrong name" },
  { value: "location", label: "Wrong location" },
  { value: "website", label: "Wrong website" },
  { value: "founded", label: "Wrong founding year" },
  { value: "type", label: "Wrong type" },
  { value: "status", label: "Closed / renamed / merged" },
  { value: "duplicate", label: "Duplicate institution" },
  { value: "other", label: "Something else" },
];

function CorrectionDialog({ instId, inst }) {
  const [open, setOpen] = useState(false);
  const [field, setField] = useState("name");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const showValue = ["name", "location", "website", "founded", "type"].includes(field);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/institutions/${instId}/corrections`, { field, suggested_value: value, note, contact });
      toast.success("Thank you — your correction has been sent for review");
      setOpen(false); setValue(""); setNote(""); setContact("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" className="rounded-full gap-1.5" data-testid="suggest-correction-btn"><Flag size={15} /> Suggest a correction</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="correction-dialog">
        <DialogHeader>
          <DialogTitle>Suggest a correction</DialogTitle>
          <DialogDescription>Spotted something wrong about {inst.name}? Tell us and our team will review it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {CORRECTION_FIELDS.map((f) => (
              <button key={f.value} onClick={() => setField(f.value)} data-testid={`correction-field-${f.value}`}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${field === f.value ? "border-secondary bg-secondary/10 text-secondary" : "border-border/60 text-muted-foreground hover:border-secondary/50"}`}>{f.label}</button>
            ))}
          </div>
          {showValue && (
            <div><Label>Corrected value</Label><Input className="mt-1" value={value} onChange={(e) => setValue(e.target.value)} placeholder="What should it say?" data-testid="correction-value" /></div>
          )}
          <div><Label>Note / details</Label><Textarea className="mt-1 min-h-[70px]" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add any detail or a source that helps us verify." data-testid="correction-note" /></div>
          <div><Label>Your contact (optional)</Label><Input className="mt-1" value={contact} onChange={(e) => setContact(e.target.value)} placeholder="Email, in case we need to follow up" /></div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="rounded-full gap-1.5" data-testid="correction-submit"><Flag size={15} /> {busy ? "Sending…" : "Send correction"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BatchBoards({ inst }) {
  const nav = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState("");
  const [batches, setBatches] = useState([]);
  useEffect(() => {
    api.get(`/institutions/${inst.id}/batches`).then((r) => setBatches(r.data.batches || [])).catch(() => setBatches([]));
  }, [inst.id]);
  const open = (y) => { if (/^\d{4}$/.test(String(y))) nav(`/institutions/${inst.id}/batch/${y}`); };
  const quick = [currentYear, currentYear - 5, currentYear - 10, currentYear - 25, currentYear - 50];
  return (
    <div className="mt-6 rounded-3xl border border-indigo-200 bg-indigo-50/40 p-5 ring-1 ring-indigo-500/10 sm:p-6" data-testid="batch-boards">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow"><UsersThree size={24} weight="duotone" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl font-semibold">Find your batch</h2>
          <p className="text-xs text-muted-foreground">Open your Class-of-year room — classmates, teachers of your era, memories &amp; reunion planning.</p>
        </div>
      </div>

      {batches.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="batch-year-grid">
          {batches.map((b) => (
            <button key={b.year} onClick={() => open(b.year)} data-testid={`batch-year-${b.year}`}
              className="group inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white px-4 py-2 text-sm font-medium shadow-sm transition-all hover:-translate-y-0.5 hover:border-indigo-400 hover:bg-indigo-50">
              Class of {b.year}
              <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[11px] font-semibold text-indigo-700">{b.member_count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Input value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Batch year e.g. 1975" className="w-40 rounded-full" data-testid="batch-year-input" onKeyDown={(e) => e.key === "Enter" && open(year)} />
        <Button className="rounded-full gap-1.5" onClick={() => open(year)} data-testid="batch-open-btn"><UsersThree size={16} /> Open Class of {year || "…"}</Button>
        <span className="text-xs text-muted-foreground">or</span>
        <div className="flex flex-wrap gap-1.5">
          {quick.map((y) => (
            <button key={y} onClick={() => open(y)} className="rounded-full border border-indigo-200 bg-white px-3 py-1 text-xs transition-colors hover:bg-indigo-100" data-testid={`batch-quick-${y}`}>{y}</button>
          ))}
        </div>
      </div>
    </div>
  );
}

function MergeDialog({ keepInst, onMerged }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/public/search", { params: { q: q.trim(), limit: 10 } });
        setResults((data.institutions || []).filter((i) => i.id !== keepInst.id));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open, keepInst.id]);

  const toggle = (inst) => setSelected((s) => s.some((x) => x.id === inst.id) ? s.filter((x) => x.id !== inst.id) : [...s, inst]);

  const merge = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Merge ${selected.length} duplicate(s) INTO "${keepInst.name}"? Their alumni groups, courses, photos and links will move here and the duplicates will be deleted. This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/institutions/merge", { keep_id: keepInst.id, merge_ids: selected.map((s) => s.id) });
      toast.success(`Merged ${data.deleted} duplicate(s) into this institution`);
      setOpen(false); setSelected([]); setQ(""); onMerged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Merge failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" className="rounded-full gap-1.5" data-testid="merge-duplicates-btn"><ArrowsMerge size={15} /> Merge duplicates</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Merge duplicates into this record</DialogTitle>
          <DialogDescription>Search for other entries of the same institution. They'll be folded into <strong>{keepInst.name}</strong> (kept as canonical) and then removed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search duplicate institution by name…" className="pl-9 rounded-full" data-testid="merge-search" />
          </div>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="merge-selected">
              {selected.map((s) => (
                <Badge key={s.id} variant="secondary" className="gap-1 rounded-full font-normal">{s.name}
                  <button onClick={() => toggle(s)} className="ml-0.5 text-muted-foreground hover:text-destructive"><X size={12} /></button>
                </Badge>
              ))}
            </div>
          )}
          <div className="max-h-64 space-y-1 overflow-y-auto">
            {results.map((r) => {
              const on = selected.some((x) => x.id === r.id);
              return (
                <button key={r.id} type="button" onClick={() => toggle(r)} data-testid={`merge-result-${r.id}`}
                  className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${on ? "border-secondary bg-secondary/10" : "border-border/60 hover:bg-accent"}`}>
                  <Buildings size={16} className="shrink-0 text-secondary" />
                  <span className="min-w-0 flex-1"><span className="block truncate font-medium">{r.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{[r.type, r.location || r.country].filter(Boolean).join(" · ")}</span></span>
                  {on && <SealCheck size={16} weight="fill" className="text-secondary" />}
                </button>
              );
            })}
            {q.trim().length >= 2 && results.length === 0 && <p className="px-1 py-3 text-center text-sm text-muted-foreground">No other institutions match.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={merge} disabled={busy || !selected.length} className="rounded-full gap-1.5" data-testid="merge-submit"><ArrowsMerge size={15} /> {busy ? "Merging…" : `Merge ${selected.length || ""} into this`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
